/**
 * useHubVoci — le voci degli hub «Configurazione» e «Configurazione Admin», condivise dal layout.
 *
 * Il menu laterale mostra «Configurazione» (e «Configurazione Admin») come FOGLIA verso `/configurazione`
 * (`/configurazione-admin`): le pagine di configurazione (Configura Email, Elenco Banche, …) non sono più
 * nell'albero del menu. Questo elenco serve a tenerle raggiungibili e coerenti:
 *  - breadcrumb «Configurazione › titolo» (AppBreadcrumb);
 *  - voce dell'hub evidenziata nel menu mentre si è su una di quelle pagine (AppMenuItem);
 *  - ricerca rapida (PaletteRicerca), che indicizza anche queste voci.
 *
 * Ogni hub si carica una volta per utente, e solo se il menu ha la sua foglia.
 */
import { computed, watch, type Ref } from 'vue'
import { apiClient } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useMenuStore, type MenuNode } from '@/stores/menu'
import { azzeraHubVoci, hubInvalidazioni, hubStato, hubStatoAdmin, hubVoci, hubVociAdmin } from '@/composables/hubVociStato'
import { normalizzaHub, type ConfigurazioneVoce } from '@/modules/configurazione/composables/useConfigurazioneHub'
import { HUB, type HubId } from '@/modules/configurazione/lib/hubConfig'
import type { components } from '@/api/generated/schema'

export const ROUTE_HUB_CONFIGURAZIONE = HUB.configurazione.radice
export const ROUTE_HUB_ADMIN = HUB.admin.radice

export { azzeraHubVoci }

function haFoglia(nodes: MenuNode[], route: string): boolean {
  return nodes.some((n) => n.springRoute === route || (n.children?.length ? haFoglia(n.children, route) : false))
}

function senzaCoda(path: string): string {
  const p = path.split('?')[0] ?? path
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p
}

const STATO: Record<HubId, { voci: Ref<ConfigurazioneVoce[]>, stato: { caricatoPer: number | null, richiesta: number } }> = {
  configurazione: { voci: hubVoci, stato: hubStato },
  admin: { voci: hubVociAdmin, stato: hubStatoAdmin },
}

// Richiesta in corso per hub: le molte istanze del composable (una per voce di menu) ne condividono una sola.
const inCorso: Partial<Record<HubId, { utente: number, mia: number, p: Promise<void> }>> = {}

export function useHubVoci() {
  const menu = useMenuStore()
  const auth = useAuthStore()
  const haHub = computed(() => haFoglia(menu.items, HUB.configurazione.radice))
  const haHubAdmin = computed(() => haFoglia(menu.items, HUB.admin.radice))

  async function caricaHub(hub: HubId, presente: boolean): Promise<void> {
    const { voci, stato } = STATO[hub]
    const utente = auth.me?.userId ?? null
    if (!presente || utente === null) {
      // Foglia assente o logout: si svuota solo questo hub (azzeraHubVoci è del logout dello store auth).
      voci.value = []
      stato.caricatoPer = null
      stato.richiesta++
      return
    }
    if (stato.caricatoPer === utente) return
    const incorso = inCorso[hub]
    if (incorso && incorso.utente === utente && incorso.mia === stato.richiesta) return incorso.p
    // Un altro utente (o una nuova richiesta) invalida subito ciò che c'era e ogni richiesta ancora in volo.
    if (stato.caricatoPer !== null) voci.value = []
    const mia = ++stato.richiesta
    const p = richiediHub(hub, voci, stato, utente, mia)
    const voce = { utente, mia, p }
    inCorso[hub] = voce
    try {
      await p
    } finally {
      if (inCorso[hub] === voce) delete inCorso[hub] // conclusa: la prossima richiesta è nuova
    }
  }

  async function richiediHub(hub: HubId, voci: Ref<ConfigurazioneVoce[]>, stato: { caricatoPer: number | null, richiesta: number }, utente: number, mia: number): Promise<void> {
    try {
      const { data } = await apiClient.get<components['schemas']['ConfigurazioneHubDTO']>(HUB[hub].endpoint)
      if (mia !== stato.richiesta || (auth.me?.userId ?? null) !== utente) return // superata: scartata
      voci.value = normalizzaHub(data, hub).flatMap((a) => a.voci)
      stato.caricatoPer = utente
    } catch (e) {
      if (mia !== stato.richiesta) return
      // degrada: breadcrumb e menu ripiegano sul comportamento di prima
      voci.value = []
      stato.caricatoPer = null
      console.debug(`[hub-${hub}] caricamento delle voci non riuscito`, e)
    }
  }

  async function carica(): Promise<void> {
    await Promise.all([caricaHub('configurazione', haHub.value), caricaHub('admin', haHubAdmin.value)])
  }

  watch([haHub, haHubAdmin, () => auth.me?.userId, hubInvalidazioni], () => { void carica() }, { immediate: true })

  /** L'hub la cui voce ha esattamente `path` come pagina (la foglia stessa non è una voce). */
  function hubDiRoute(path: string): HubId | undefined {
    const p = senzaCoda(path)
    const cerca = (voci: ConfigurazioneVoce[]) => voci.some((v) => v.route != null && !v.classico && senzaCoda(v.route) === p)
    if (cerca(hubVoci.value)) return 'configurazione'
    if (cerca(hubVociAdmin.value)) return 'admin'
    return undefined
  }

  /** La voce di un hub (Configurazione o Admin) la cui pagina è esattamente `path`. */
  function voceDiRoute(path: string): ConfigurazioneVoce | undefined {
    const p = senzaCoda(path)
    const cerca = (voci: ConfigurazioneVoce[]) => voci.find((v) => v.route != null && !v.classico && senzaCoda(v.route) === p)
    return cerca(hubVoci.value) ?? cerca(hubVociAdmin.value)
  }

  return { voci: hubVoci, vociAdmin: hubVociAdmin, haHub, haHubAdmin, voceDiRoute, hubDiRoute }
}

/** Titolo di una voce nella lingua dell'utente: `menu.<id>`, altrimenti il testo del backend. */
export function titoloVoceHub(v: ConfigurazioneVoce, t: (k: string) => string, te: (k: string) => boolean): string {
  return te(`menu.${v.idFunzione}`) ? t(`menu.${v.idFunzione}`) : v.titolo
}
